/**
 * ENG-5441 — circuit breaker for manager-initiated session restarts.
 *
 * Today's Scout incident on prod-integritylabs-agt-aws-1 respawned an
 * agent every ~55s for hours (~100 spawns/hr) because day-rollover
 * ([ENG-5431]) repeated indefinitely. Each respawn burned subscription
 * budget, flooded pane.log, drowned manager.log signal, and re-floods
 * `/host/direct-chat/poll`. The per-MCP reaper has a give-up cap
 * ([ENG-5279]) and the [ENG-5286] alarm fires when the cap-attempts
 * cross a threshold — but neither *stopped* the whole-agent loop.
 *
 * This breaker sits at the manager's spawn gate. Every manager-initiated
 * stop (day-rollover, auth-tuple change, reaper trigger, hot-reload
 * .mcp.json restart, channel-set restart) records a restart event. When
 * `> AGT_RESTART_BREAKER_MAX` events land inside the sliding
 * `AGT_RESTART_BREAKER_WINDOW_MS` window, the agent's breaker trips:
 * the manager stops respawning until an operator clears the trip (by
 * resuming the agent in the webapp / via the CLI).
 *
 * ENG-7560 — reason-aware tallies. Adding one integration fans out into a
 * burst of restarts (doorbell respawn + managed-toolkit convergence +
 * `.mcp.json` drift + bind-remediation), all tagged provisioning-reload
 * reasons; counting that burst on the tight crash bar auto-paused Pepper on
 * an Outlook add. So provisioning-reload reasons (see
 * {@link isProvisioningReloadReason}) are counted on a SEPARATE, looser tally
 * (`AGT_RESTART_BREAKER_PROVISIONING_MAX` in
 * `AGT_RESTART_BREAKER_PROVISIONING_WINDOW_MS`); a routine add can't trip it,
 * but sustained provisioning thrash still accumulates and trips+pages. Crash /
 * all other reasons keep the tight bar, counted exactly as before. Every event
 * is still recorded — only the trip threshold is class-aware — so relaxing the
 * bar can never silently defeat the breaker.
 *
 * ENG-7812: per-integration provisioning buckets. The single looser provisioning
 * tally still auto-paused a HEALTHY agent mid-onboarding: bringing up 5+
 * integrations legitimately fires 6+ provisioning-reload restarts in 30 min
 * (each add fans out into `integration-change` + `hot-reload-mcp`), clearing the
 * flat >5 bar even though nothing is wrong. The bar couldn't tell "many distinct
 * integrations each binding once" (onboarding) from "one integration looping"
 * (the pathology). So the provisioning trip is now bucketed by the integration a
 * restart is attributable to ({@link RestartEvent.integrationKey}, supplied by the
 * manager from a same-window membership delta) and fires on the LARGEST single
 * bucket, not the total. Onboarding N distinct adds ⇒ N buckets of ~1 ⇒ no trip;
 * one integration re-binding >5 times ⇒ one overflowing bucket ⇒ trip+page.
 * Unattributed provisioning reloads (a reprovision of an unchanged set) share one
 * `''` bucket that still trips on its own, so the breaker keeps its teeth.
 *
 * Pure state, no I/O — the caller (manager-worker.ts) owns serialisation
 * to `manager-state.json` so a manager restart preserves trip state and
 * doesn't accidentally clear a tripped breaker. Tests drive `now()` via
 * the constructor to avoid sleeping in CI.
 */

import {
  RESTART_BREAKER_PROVISIONING_MAX,
  RESTART_BREAKER_PROVISIONING_WINDOW_MS,
  RESTART_BREAKER_OPERATOR_MAX,
  RESTART_BREAKER_SCHEDULED_MAX,
  RESTART_BREAKER_SCHEDULED_WINDOW_MS,
  RESTART_BREAKER_OPERATOR_COMMAND_MAX,
  RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS,
  RESTART_BREAKER_NEW_AGENT_GRACE_MS,
  RESTART_BREAKER_NEW_AGENT_GRACE_CEILING,
  BIND_FAILURE_QUARANTINE_THRESHOLD,
  MIN_PROVISIONING_MAX_FOR_QUARANTINE_ORDERING,
} from '@augmented/core';

export interface RestartBreakerOptions {
  /** Max restart events allowed inside the window; the (max+1)th trips. Default 2 (env: AGT_RESTART_BREAKER_MAX). */
  max?: number;
  /** Sliding window in ms. Default 600_000 = 10 min (env: AGT_RESTART_BREAKER_WINDOW_MS). */
  windowMs?: number;
  /**
   * ENG-7560: max provisioning-reload restart events allowed inside the
   * provisioning window; the (max+1)th trips. Default 5
   * (env: AGT_RESTART_BREAKER_PROVISIONING_MAX). Looser than `max` so a single
   * integration-add's 3-4 restart burst can't pause the agent.
   */
  provisioningMax?: number;
  /**
   * ENG-7560: sliding window in ms for the provisioning-reload tally. Default
   * 1_800_000 = 30 min (env: AGT_RESTART_BREAKER_PROVISIONING_WINDOW_MS). Wider
   * than `windowMs` so sustained provisioning thrash still accumulates and trips.
   */
  provisioningWindowMs?: number;
  /**
   * ENG-8388: budget for provisioning reloads a console user demonstrably asked
   * for, counted per integration over `provisioningWindowMs`. Defaults to
   * RESTART_BREAKER_OPERATOR_MAX (looser than `provisioningMax`); env override
   * `AGT_RESTART_BREAKER_OPERATOR_MAX`.
   */
  operatorMax?: number;
  /**
   * ENG-9138: how many platform-scheduled restarts (the `scheduled` class) may
   * land inside `scheduledWindowMs` before the breaker raises a LOOP SIGNAL.
   *
   * Not a trip threshold — the scheduled class never pauses an agent. Crossing
   * this sets `scheduledLoop` on the RecordResult, which the manager reports as
   * a suppressed pause. Defaults to RESTART_BREAKER_SCHEDULED_MAX (3); env
   * override `AGT_RESTART_BREAKER_SCHEDULED_MAX`.
   */
  scheduledMax?: number;
  /**
   * ENG-9138: sliding window for the scheduled tally. Defaults to
   * RESTART_BREAKER_SCHEDULED_WINDOW_MS (60 min); env override
   * `AGT_RESTART_BREAKER_SCHEDULED_WINDOW_MS`.
   */
  scheduledWindowMs?: number;
  /**
   * ENG-10066: how many operator-commanded restarts (the `operator-command`
   * class) may land inside `operatorCommandWindowMs` before the breaker raises a
   * LOOP SIGNAL. Not a trip threshold — this class never pauses an agent.
   * Crossing it sets `operatorCommandLoop` on the RecordResult. Defaults to
   * RESTART_BREAKER_OPERATOR_COMMAND_MAX (5); env override
   * `AGT_RESTART_BREAKER_OPERATOR_COMMAND_MAX`.
   */
  operatorCommandMax?: number;
  /**
   * ENG-10066: sliding window for the operator-command tally. Defaults to
   * RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS (60 min); env override
   * `AGT_RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS`.
   */
  operatorCommandWindowMs?: number;
  /** Injectable clock for tests. */
  now?: () => number;
  /**
   * ENG-7891 slice 5b (ADR-0049 durable-state rule): resolve a code_name to the
   * rename-stable DURABLE key used for the events/trips maps and their
   * manager-state.json persistence. On an id-keyed host this returns the agent's
   * stable agent_id, so a trip that paused a crash-looping agent survives a
   * codename rename instead of being stranded under the old codename (which would
   * silently un-pause the agent the operator meant to keep down). Defaults to
   * identity, so the legacy fleet (no symlink) keys by code_name exactly as
   * before — a pure no-op until the id-keyed layout is armed. Injected rather than
   * importing `agentRuntimeKey` here so the class stays pure "no I/O" state for
   * its unit tests; the manager wires in the real (fs-backed) resolver.
   */
  runtimeKey?: (codeName: string) => string;
  /**
   * ENG-8690: epoch-ms the agent was CREATED, per code_name, or undefined when
   * the manager does not know it.
   *
   * Injected as a lookup rather than passed to `record()` so the grace cannot be
   * forgotten at one of the many call sites that record a restart - the breaker
   * asks, every time.
   *
   * Returning undefined means NO grace. That direction is deliberate and is the
   * only safe one: an agent whose age we cannot establish must be governed by
   * the breaker, not exempted from it. The opposite default would silently
   * disable provisioning trips fleet-wide the moment this lookup broke, and a
   * disabled safety net that still reports as armed is worse than none.
   */
  agentCreatedAt?: (codeName: string) => number | undefined;
  /**
   * ENG-8690: how long after creation provisioning churn cannot pause an agent.
   * Defaults to RESTART_BREAKER_NEW_AGENT_GRACE_MS (24h); env override
   * `AGT_RESTART_BREAKER_NEW_AGENT_GRACE_MS`. Zero disables the grace entirely.
   */
  newAgentGraceMs?: number;
  /**
   * ENG-8690: provisioning-shaped restarts inside `provisioningWindowMs` that
   * will pause even an agent inside its grace. Defaults to
   * RESTART_BREAKER_NEW_AGENT_GRACE_CEILING (30); env override
   * `AGT_RESTART_BREAKER_NEW_AGENT_GRACE_CEILING`.
   *
   * Floored at `provisioningMax` in the constructor: a ceiling BELOW the normal
   * bar would make the grace strictly worse than no grace at all, pausing new
   * agents sooner than mature ones - an inversion that is easy to reach by
   * setting one env var without the other.
   */
  newAgentGraceCeiling?: number;
}

/** All known restart trigger sources. Adding a new source? Add it here so the trip detail is self-describing. */
export type RestartReason =
  | 'day-rollover'
  | 'auth-tuple-change'
  | 'model-change'
  | 'mcp-presence-reaper'
  | 'hot-reload-mcp'
  // ENG-7239: a .mcp.json drift restart whose changed server keys are ALL
  // managed-MCP/brokered toolkits (Composio and friends). Split from the
  // generic 'hot-reload-mcp' so the breaker carve-out for a flapping managed-MCP
  // set applies ONLY to managed-MCP churn, never to a co-occurring channel or
  // credential restart that happens to share the cool-off (CodeRabbit PR #2870).
  // Gated and respawn-verified identically to 'hot-reload-mcp'.
  | 'managed-mcp-churn'
  // ENG-7576: a dashboard-initiated integration add/remove reload
  // (host_agents.restart_reason='integration-change', mirroring the API's
  // RESTART_REASON_INTEGRATION_CHANGE) serviced by the poll's restart-consumption
  // loop. Before ENG-7576 that stop passed NO breaker reason, so the most common
  // provisioning restart was invisible to the breaker and to restart telemetry.
  // Counted on the LOOSER provisioning tally like its hot-reload siblings; a
  // plain operator 'manual' dashboard restart stays uncounted.
  // Deliberately NOT in update-window-gate.ts GATEABLE_RESTART_REASONS (nor the
  // window-exempt set): integration loads bypass the maintenance window entirely
  // (ENG-6568 - gating this class once left valai ~30h on stale tools). Do not
  // "tidy" it into that allowlist.
  | 'integration-change'
  // ENG-8388: the SAME reload as 'integration-change', but one the API could
  // prove a human asked for — the host_agents stamp carried
  // `restart_actor: 'user'`, set only on a route with an authenticated console
  // user behind it. Split out as its own reason (not a flag on the event) so it
  // gets its own class and its own looser budget: the breaker exists to catch a
  // LOOP, and six reloads from six deliberate clicks is not one. Two live
  // failures forced this: a new customer's first agent auto-paused inside ten
  // minutes for connecting four integrations (ENG-8204), and `don` auto-paused
  // for six credential re-entries while someone was debugging that very
  // integration (ENG-8388) — the worst possible moment, because a paused agent
  // binds nothing, so the pause destroys your ability to tell whether the fix
  // worked. An autonomous storm carries no user actor and stays
  // 'integration-change', on the unchanged tally.
  | 'operator-integration-change'
  | 'channel-set-change'
  // ENG-5858: effective sender_policy flipped on either the org or
  // per-agent column. The slack-channel / teams-channel MCP children read
  // SLACK_SENDER_POLICY (and friends) from process.env once at boot, so
  // the rewrite of .mcp.json on its own doesn't propagate — the running
  // child keeps its stale env and stale gate. This restart class fires
  // independently of the .mcp.json value-only drift suppression
  // (ENG-5537) so composio URL rotation can't accidentally trigger it.
  | 'sender-policy-change'
  // ENG-9802: the telegram chat policy changed — the allowlist,
  // private-chats-only, or the peer kill switch. Identical last-hop gap: the
  // telegram-channel MCP child reads TELEGRAM_ALLOWED_CHATS (and every other
  // key it has) from process.env once at boot. Keyed on a classified policy
  // subset so credential churn and cosmetic edits cannot fire it.
  | 'telegram-policy-change'
  // ENG-9861: the agent's STANDING RULES changed. The same last-hop gap one
  // layer up: the policy reasons above are read by an MCP child from its env,
  // this is read by Claude Code itself from CLAUDE.md, once, at session start.
  // A rule added mid-session is on disk and inert until an unrelated restart,
  // while the person has been told it is in force. Keyed on a hash of the
  // RENDERED rules section, so unrelated CLAUDE.md regeneration (skills index,
  // integrations, guardrails) cannot fire it.
  | 'agent-rules-change'
  // ENG-6024: a Teams behaviour field (thread_auto_follow,
  // channel_response_mode, adaptive_cards_*) changed via the webapp's
  // Advanced panel. Same last-hop gap as sender-policy-change: the
  // teams-channel MCP child reads MSTEAMS_THREAD_AUTO_FOLLOW (and friends)
  // from process.env once at boot, so the .mcp.json rewrite alone doesn't
  // propagate. Keyed on a behaviour-subset hash, so token/credential churn
  // in the wider channel config can't fire it.
  | 'channel-behaviour-change'
  | 'manual'
  // A channel MCP drained a restart flag from ~/.augmented/restart-flags/.
  //
  // ENG-10066 — WHAT ACTUALLY WRITES THIS. Every writer of `<codeName>.flag` in
  // this repo is a HUMAN typing `/restart`: slack-channel.ts (the slash-command
  // handler and the message interceptor, both gated on SLACK_ALLOWED_USERS) and
  // telegram-channel.ts. The only other writer is the in-session agent's own
  // self-restart, which carries `source: 'agent'` and is split off to
  // 'agent-requested' before it ever reaches this reason. So this reason means
  // "a person asked for a restart", and nothing else.
  //
  // This comment used to claim the flag was written "after an unrecoverable
  // runtime error", citing ENG-5963 — which is about maintenance windows and has
  // nothing to do with restart flags. No such machine writer exists or ever did.
  // The claim was load-bearing: it justified counting this reason on the tight
  // CRASH bar, so the third `/restart` inside ten minutes auto-paused the agent
  // the operator was trying to revive (razorclaw-sales, 2026-09-05, seven times
  // in one day). It is now classified 'operator-command' and does not trip.
  //
  // IF YOU ADD A MACHINE WRITER: give it its OWN reason. Do not reuse this one —
  // its whole meaning is that a human asked, and a self-writing channel is
  // exactly the loop the breaker should still bound.
  //
  // Still RECOVERY (a person restarts because something is wrong now), so it
  // remains deliberately OUT of the maintenance-window gate's allowlist and
  // restarts immediately.
  | 'channel-restart-flag'
  | 'stale-mcp'
  // ENG-6203: a SESSION-level self-heal — the manager's restart-verifier reached
  // `unverified final` (a hot-reload restart that never respawned a healthy
  // session and never re-bound its MCP tools) and is forcing one more gated
  // re-respawn. Counts toward the breaker so a session that repeatedly fails to
  // respawn healthy is bounded (original restart + one remediation, then trip →
  // visible/circuit-tripped) rather than looping forever. Session-scoped, unlike
  // the per-server `mcp-presence-reaper` reason (which ENG-5547 keeps off the
  // breaker so one bad tool can't pause the whole agent).
  | 'bind-remediation'
  // ENG-6229: the in-session agent called `request_restart` after confirming
  // with the human (its existing ask_user HITL) that it needs a reload to pick
  // up a change it can SEE is missing but the manager's desired-state drift
  // detection structurally cannot (e.g. a realized-state MCP-binding fault, or
  // a hot-reload gap). RECOVERY class like `channel-restart-flag` — restarts
  // immediately, NOT held behind the maintenance window — because the agent
  // only asks when it's actively stuck serving someone. Counts toward the
  // breaker so a structurally-confused agent that keeps asking (a restart it
  // perceives as needed but that a restart cannot fix) is bounded and trips
  // into operator-cleared pause rather than looping.
  | 'agent-requested'
  // ENG-7541: a value-only re-mint of an already-present env-only integration
  // credential. GitHub is the canonical case: the App installation token is
  // re-minted server-side on every provision poll (POST
  // /app/installations/{id}/access_tokens is NOT idempotent, so each cache-miss
  // hands back a brand-new token string for the SAME logical credential). The
  // manager sees a changed GITHUB_ACCESS_TOKEN value, respawns the env-only
  // session to pick it up (ENG-7510), and the next poll re-mints again → a
  // respawn every 2-3 min. This is NOT a crash and NOT a membership change (the
  // integration set is unchanged), so it must NEVER be able to pause the agent.
  // Split from 'hot-reload-mcp' so it lands on its OWN non-tripping breaker class
  // (restartReasonClass → 'credential-rotation'): still RECORDED for forensics +
  // the churn signal, but excluded from every trip tally. A benign per-poll
  // re-mint and a pathological storm share the SAME rate, so no finite threshold
  // can separate them — the real cure is a durable shared token cache (ENG-7541
  // fix 1) / a no-respawn credential reload (ENG-7510 follow-up), not a breaker
  // threshold. Emitted ONLY when every changed env-only var is a value-only
  // rotation of an already-present var; any add/remove keeps the membership
  // 'hot-reload-mcp' class. Generic, not GitHub-specific — the live-registry
  // reconcile sibling (ENG-7559) shares it.
  | 'credential-rotation'
  // ENG-7949: a mcp-presence-reaper restart where at least one of the declared-
  // but-dead MCPs (activeKeys) is a known CHANNEL server (slack/telegram/
  // msteams/direct-chat/...), as opposed to a tool MCP (composio_*/xero) or an
  // unrecognised key. A dead channel MCP makes the agent unreachable on that
  // channel — indistinguishable from a crashed agent from the user's side — so
  // this reason is window-exempt (update-window-gate.ts) even though the
  // GENERIC 'mcp-presence-reaper' reason is not: a routine tool-MCP reaper
  // restart still politely waits for the off-peak window, but a dead channel
  // must not sit silent for up to ~14h until the window opens. Still subject to
  // defer-until-idle (never interrupts a live turn) and does NOT change breaker
  // counting — the reaper's own single-vs-multi-server breakerReason logic
  // (reaperRestartBreakerReason) is unchanged; this is a GATE reason only, kept
  // distinct from the breaker-count reason exactly like the existing
  // 'mcp-presence-reaper' gate/breaker split (see stopSession in
  // manager-worker.ts and its ENG-6264 comment).
  | 'channel-mcp-down'
  | 'unknown';

/**
 * ENG-5547: decide whether an mcp-presence-reaper restart should count
 * toward the agent-wide circuit breaker.
 *
 * A restart attributable to a SINGLE persistently-failing MCP server is
 * already governed by that server's own ENG-5279 give-up cap (3 cycles →
 * the integration is marked unhealthy via /host/integration-health and
 * dropped from the next .mcp.json), which isolates the one broken tool and
 * lets the agent keep running degraded. Counting it would let the breaker
 * (default max 2) trip on the 3rd restart and pause the ENTIRE agent for one
 * bad integration — racing, and usually beating, the give-up that would have
 * isolated it. That sledgehammer is exactly what auto-paused phil.
 *
 * A restart where MULTIPLE distinct servers are simultaneously missing is a
 * genuine multi-cause storm the per-server cap can't see (the rotating-MCP
 * case ENG-5441 exists for), so it still counts.
 *
 * Returns the {@link RestartReason} to record (the restart counts toward the
 * breaker), or `undefined` to restart WITHOUT counting.
 */
export function reaperRestartBreakerReason(
  activeKeys: readonly string[],
): RestartReason | undefined {
  return activeKeys.length >= 2 ? 'mcp-presence-reaper' : undefined;
}

/**
 * ENG-7560: which breaker tally a restart reason counts against.
 *
 * `provisioning` — a reload driven by a routine operator/provisioning action
 * (adding an integration binds new MCP tools). One integration-add fans out
 * into several restarts across adjacent poll cycles (doorbell respawn +
 * managed-toolkit convergence + `.mcp.json` drift + bind-remediation), all
 * tagged with these reasons. Counting that burst on the tight crash tally
 * auto-paused Pepper on an Outlook add. These get their own LOOSER tally so a
 * single add can't trip, while sustained provisioning thrash still trips+pages.
 *
 * `crash` — everything else. A crash loop / structurally-stuck agent must still
 * trip the tight ENG-5441 bar, so all non-provisioning reasons (including
 * `agent-requested`, `channel-restart-flag`, `day-rollover`, the multi-server
 * `mcp-presence-reaper`) stay on it, counted exactly as before.
 *
 * The split is reason-scoped, NOT time-scoped: a crash restart landing inside a
 * provisioning burst still counts on the crash tally, so a genuine crash loop
 * masquerading under provisioning churn is not coalesced away.
 *
 * `credential-rotation` — ENG-7541: a value-only server-side re-mint of an
 * already-present env-only credential (see the 'credential-rotation'
 * RestartReason). Its own class precisely BECAUSE the provisioning tally can't
 * help it: the provisioning bar still trips on sustained churn, and a per-poll
 * re-mint is sustained churn by rate — indistinguishable from a storm — so
 * folding it into `provisioning` would just move the auto-pause, not remove it.
 * This class is RECORDED (forensics + classCounts visibility) but NEVER trips on
 * its own; a co-occurring crash / provisioning restart still trips its own tally.
 *
 * Shared predicates so the sibling churn-family tickets inherit one
 * classification: the live-registry reconcile sibling (ENG-7559) uses the same
 * credential-rotation class.
 */
export type RestartReasonClass =
  | 'crash'
  | 'provisioning'
  | 'credential-rotation'
  | 'self-healing'
  // ENG-8388: a provisioning reload the API proved a console user asked for.
  // Counted on its own, looser tally (RESTART_BREAKER_OPERATOR_MAX) instead of
  // the shared provisioning one, because the thing the breaker is built to catch
  // is a loop and a burst of deliberate clicks is not one.
  | 'operator-config'
  // ENG-9138: a restart the PLATFORM ITSELF scheduled — today that is the daily
  // session roll ('day-rollover'). Recorded and surfaced like every other class,
  // but excluded from every trip tally: see SCHEDULED_REASONS for why pausing on
  // one is a category error rather than a tuning problem.
  | 'scheduled'
  // ENG-10066: restarts a human explicitly commanded (a channel `/restart`).
  // Like `scheduled`, fully recorded and never a trip — see
  // OPERATOR_COMMAND_REASONS.
  | 'operator-command';

/**
 * ENG-7577: membership test for a rehydrated `trippedClass`.
 *
 * Derived from a `Record<RestartReasonClass, true>` rather than a `Set` literal
 * so it really is compiler-locked: a new union member that is not listed here is
 * a type error, where a `Set<RestartReasonClass>([...])` would have compiled
 * happily with the member missing (CodeRabbit on PR #3861).
 */
const KNOWN_REASON_CLASS_MAP: Record<RestartReasonClass, true> = {
  crash: true,
  provisioning: true,
  'credential-rotation': true,
  // ENG-8388: provisioning reloads a human demonstrably asked for. Same
  // Record-not-Set reasoning as above — a new class cannot be silently omitted.
  'operator-config': true,
  // ENG-8215: the self-healing class (bind-remediation - the manager's OWN
  // repair attempts, which must never count toward a trip). Adding it here is
  // forced by the Record type, which is exactly why that shape was chosen over
  // a Set literal (ENG-7577, CodeRabbit on #3861) - a new union member cannot
  // be silently omitted from the rehydration allowlist.
  'self-healing': true,
  // ENG-9138: the platform-scheduled class. Same Record-not-Set reasoning: the
  // compiler forces every new class into this allowlist, so a `trippedClass`
  // rehydrated from manager-state.json can never name a class this build does
  // not know about.
  scheduled: true,
  // ENG-10066: the operator-command class. Same Record-not-Set reasoning again —
  // the compiler forces every new class into this allowlist.
  'operator-command': true,
};
const KNOWN_REASON_CLASSES: ReadonlySet<RestartReasonClass> = new Set(
  Object.keys(KNOWN_REASON_CLASS_MAP) as RestartReasonClass[],
);

const PROVISIONING_RELOAD_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'hot-reload-mcp',
  'managed-mcp-churn',
  // ENG-8215: 'bind-remediation' MOVED OUT of this set into SELF_HEALING_REASONS.
  // It is the manager repairing itself, not a config reload, and counting it
  // meant the harder the platform tried to fix an agent the more certain it
  // became that it should give up. Deliberately NOT re-added here.
  // ENG-7576: the dashboard integration-add stop itself - the first restart of
  // the very burst this class exists for, previously uncounted entirely.
  'integration-change',
  // ENG-7771: a sender_policy delivery restart is a deliberate config-delivery
  // reload (operator changed the policy, or the fail-closed first-poll verify
  // after a manager restart with no persisted baseline), not a crash. On the
  // tight crash tally, a host whose sender-policy-baseline.json write keeps
  // failing (disk full, permissions) would convert the fail-closed restart
  // into repeated crash tallies across a multi-deploy day and trip the
  // breaker - taking the agent DOWN, which is strictly worse than the stale
  // policy the restart exists to fix. The looser provisioning tally still
  // trips + pages on sustained thrash.
  'sender-policy-change',
  // ENG-9802: same argument, same failure mode — a host whose baseline write
  // keeps failing must not convert a fail-closed policy restart into a breaker
  // trip that takes the agent down.
  'telegram-policy-change',
  // ENG-9861: and again for standing rules. The fail-closed first-poll restart
  // has exactly the same shape, so it needs the same protection from a host
  // whose sidecar write keeps failing.
  'agent-rules-change',
  // ENG-10510 (Obi-Wan on #6313): a channel-set ADD is a config reload, not a
  // crash, and it was landing on the crash tally purely by falling through.
  // It is 'integration-change's exact sibling - both are "a channel or server
  // was added, re-render .mcp.json and respawn" - and it was never argued for
  // the crash class, it simply was not in this set.
  //
  // This is the ENG-9138 / ENG-10066 species verbatim: a PLATFORM-MANUFACTURED
  // restart counted as though the agent had crashed. ENG-9138 cost katie 8h16m
  // when day-rollover fell through the same way; ENG-10066 cost an operator an
  // auto-pause when a channel /restart did. Both were fixed by naming the
  // reason rather than leaving it to the catch-all, and this is the third.
  //
  // WHY NOW. Until ENG-10510 this restart only ever followed a human adding a
  // channel, so the crash tally was wrong but rarely reached: a person adding
  // two channels inside ten minutes is uncommon and self-limiting. Channel
  // auto-resume is the first thing that fires it UNATTENDED, on a timer, on
  // any agent with a quarantined channel. An agent with a second crash-class
  // restart in the same ten minutes (the presence reaper's
  // 'mcp-presence-reaper', for one) would cross DEFAULT_MAX and auto-pause
  // with no human anywhere in the loop - which is precisely the ENG-8215 harm
  // that feature's 2h floor exists to prevent.
  //
  // Counted, not exempt: provisioning is a LOOSER tally, not an absent one, so
  // sustained channel-set thrash still trips and still pages.
  'channel-set-change',
]);

export function isProvisioningReloadReason(reason: RestartReason): boolean {
  return PROVISIONING_RELOAD_REASONS.has(reason);
}

// ENG-7541: reasons that must NEVER trip the breaker on their own. A value-only
// server-side credential re-mint (see the 'credential-rotation' RestartReason)
// respawns the env-only session but is neither a crash nor a genuine membership
// change; its benign per-poll cadence is indistinguishable BY RATE from a
// pathological storm, so it gets its own class that is RECORDED (forensics +
// classCounts visibility) yet excluded from every trip tally. Only the pure
// credential-rotation reason is non-tripping — a co-occurring crash or
// provisioning restart still trips its own tally independently.
const CREDENTIAL_ROTATION_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'credential-rotation',
]);

export function isCredentialRotationReason(reason: RestartReason): boolean {
  return CREDENTIAL_ROTATION_REASONS.has(reason);
}

// ENG-8215: a SELF-HEALING action must never be able to trip the breaker.
// 'bind-remediation' is by definition the manager attempting a repair; counting
// it meant that the harder the platform tried to fix an agent, the more certain
// it became that it should give up. Live proof (sherlock, agt-demo-1,
// 2026-07-28): six provisioning restarts on one integration, FIVE of them
// bind-remediation, agent auto-paused with nobody having changed any config.
//
// Modelled on the ENG-7541 credential-rotation class: RECORDED and surfaced in
// classCounts (so the repair loop stays visible to operators and forensics), but
// excluded from every trip tally. A co-occurring crash or provisioning restart
// still trips its own tally independently.
//
// This is only safe BECAUSE quarantine now fires first (the derived
// BIND_FAILURE_QUARANTINE_THRESHOLD): without it, a permanently-unbindable MCP
// would retry forever with nothing to stop it. The two changes are a pair.
// 'hot-reload-mcp' deliberately does NOT join this set - it is a real config
// reload, and exempting it would blind the breaker to genuine .mcp.json flap.
const SELF_HEALING_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'bind-remediation',
]);

export function isSelfHealingReason(reason: RestartReason): boolean {
  return SELF_HEALING_REASONS.has(reason);
}

// ENG-9138: restarts the PLATFORM ITSELF scheduled. Never trip on their own.
//
// THE LIVE FAILURE. On 2026-08-18 `katie` (acquire-intelligence — a CUSTOMER
// agent) took three 'day-rollover' restarts inside ten minutes. day-rollover
// fell through every classifier above into the catch-all `crash` class, whose
// bar is 2, so the platform's own daily maintenance auto-paused a customer's
// agent. She stayed down 8h16m. Nobody was paged, for a second and separate
// reason (see ENG-9138 AC3 and the trip route's alert re-arm).
//
// WHY THIS IS A CATEGORY ERROR, NOT A THRESHOLD TOO LOW. The crash tally asks
// "is this agent falling over by itself?". A day rollover is not the agent
// doing anything — it is the manager tearing the session down on a schedule, to
// mint the new day's session id. Answering "is the agent unhealthy?" with
// evidence the platform manufactured is unsound at ANY threshold: raise the bar
// to 5 and a rollover loop that fires 6 times still pauses a healthy agent, for
// a reason that has nothing to do with the agent. So this takes the ENG-7541 /
// ENG-8215 treatment — RECORDED and fully visible in `classCounts`, excluded
// from every trip tally.
//
// THE LOOP IS STILL CAUGHT, IT JUST DOES NOT PAUSE. A correct rollover fires
// once per agent per day; three inside an hour is a platform bug. That is what
// RESTART_BREAKER_SCHEDULED_MAX watches for, and crossing it raises a
// `scheduledLoop` on the RecordResult so the manager reports it as a suppressed
// pause — a page about a broken rollover, instead of a dead customer agent.
//
// A co-occurring crash or provisioning restart still trips its own tally
// independently, exactly as with the other two non-tripping classes.
const SCHEDULED_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'day-rollover',
]);

export function isScheduledRestartReason(reason: RestartReason): boolean {
  return SCHEDULED_REASONS.has(reason);
}

/**
 * ENG-10066: restarts a HUMAN explicitly commanded.
 *
 * Today that is exactly one reason: a channel `/restart`. See the
 * 'channel-restart-flag' docblock in the RestartReason union for why that reason
 * means "a person asked" and cannot mean anything else today.
 *
 * Non-tripping, for the ENG-8388 reason stated one class along — "the breaker
 * exists to catch a LOOP, and six reloads from six deliberate clicks is not
 * one" — and for a sharper one of its own: a `/restart` is what somebody reaches
 * for when the agent is ALREADY broken. Counting it on the crash bar meant the
 * platform's response to "a human is trying to recover this agent" was to pause
 * the agent, and then to require a human to un-pause it. The operator's third
 * attempt to fix an agent is not evidence that the agent should be taken away
 * from them.
 *
 * RECORDED and surfaced in `classCounts` like every other class, and tallied for
 * the loop signal below, so a genuinely pathological restart rate is still
 * visible — it just gets reported instead of pausing a customer's agent.
 *
 * DELIBERATELY NOT HERE: 'agent-requested'. ENG-6229 wants a structurally
 * confused agent that keeps asking for restarts to be bounded, and that is a
 * machine in a loop, not a person with a keyboard. It stays on the crash bar.
 */
const OPERATOR_COMMAND_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'channel-restart-flag',
]);

export function isOperatorCommandReason(reason: RestartReason): boolean {
  return OPERATOR_COMMAND_REASONS.has(reason);
}

/**
 * ENG-8388: provisioning reloads carrying proof that a console user asked for
 * them. Kept OUT of PROVISIONING_RELOAD_REASONS deliberately — membership there
 * is what feeds the shared per-integration tally, and the whole point is that
 * these are counted separately. They remain fully recorded and surfaced in
 * `classCounts`.
 */
const OPERATOR_CONFIG_REASONS: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'operator-integration-change',
]);

export function isOperatorConfigReason(reason: RestartReason): boolean {
  return OPERATOR_CONFIG_REASONS.has(reason);
}

/**
 * ENG-8388 (CodeRabbit on PR #4033): does this reason's tally bucket PER
 * INTEGRATION?
 *
 * Both `record()` (which pins the key onto the event) and the manager's caller
 * (which RESOLVES the key to pass in) must agree, and they are in different
 * files. They did not: `record()` bucketed provisioning + operator-config,
 * while the caller resolved a key only for provisioning. Since
 * 'operator-integration-change' is not a provisioning reason, every operator
 * reload reached the breaker with no key and shared the '' bucket — making the
 * operator-config bucketing unreachable, and letting OPERATOR_MAX+1 DISTINCT
 * integration adds auto-pause an agent during ordinary first-time setup.
 *
 * One exported predicate, used by both sides, so the two can no longer drift.
 * This is deliberately NOT a parity test over two hand-maintained lists (the
 * ENG-8214 trap): there is now only one list to maintain.
 */
/**
 * ENG-10510 (obi-wan `f4152857336e`): a CHANNEL reason counts on the
 * provisioning TALLY but must not be attributed to an INTEGRATION.
 *
 * Adding 'channel-set-change' to PROVISIONING_RELOAD_REASONS was right for the
 * tally - it is 'integration-change's sibling and belongs on the looser budget -
 * and it silently enrolled the reason in per-integration BUCKETING too, because
 * this predicate was defined as provisioning-or-operator-config. The caller
 * attaches `agentState.provisioningAttribution` - the definition_id(s) whose
 * membership changed - for the whole 30-minute provisioning window, so a
 * channel add (or an auto-resume trial restart) landing inside that window was
 * recorded into an integration's bucket.
 *
 * Two consequences, both of them a wrong name on a real signal: the
 * per-integration bar (RESTART_BREAKER_PROVISIONING_MAX) is reached one restart
 * sooner for an integration that did not churn, and the early-warning line plus
 * the trip's `status_message` name that integration as the culprit for a
 * restart it did not cause. A channel add is never attributable to an
 * integration.
 *
 * Excluded here rather than removed from PROVISIONING_RELOAD_REASONS, because
 * the tally classification is the part that was correct. Channel reasons stay
 * on the provisioning budget and fall into the shared unattributed bucket,
 * which still trips.
 */
const NEVER_INTEGRATION_BUCKETED: ReadonlySet<RestartReason> = new Set<RestartReason>([
  'channel-set-change',
]);

export function isIntegrationBucketedReason(reason: RestartReason): boolean {
  if (NEVER_INTEGRATION_BUCKETED.has(reason)) return false;
  return isProvisioningReloadReason(reason) || isOperatorConfigReason(reason);
}

export function restartReasonClass(reason: RestartReason): RestartReasonClass {
  if (isCredentialRotationReason(reason)) return 'credential-rotation';
  if (isSelfHealingReason(reason)) return 'self-healing';
  if (isOperatorConfigReason(reason)) return 'operator-config';
  // ENG-9138: checked before the `crash` fallthrough. 'day-rollover' is not in
  // PROVISIONING_RELOAD_REASONS, so without this line it lands in the catch-all
  // crash class — which is precisely how the platform's own daily maintenance
  // came to auto-pause katie.
  if (isScheduledRestartReason(reason)) return 'scheduled';
  // ENG-10066: likewise checked before the `crash` fallthrough. A channel
  // /restart is not in PROVISIONING_RELOAD_REASONS either, so without this line
  // it lands in the catch-all crash class — which is precisely how an operator
  // recovering an agent came to auto-pause it.
  if (isOperatorCommandReason(reason)) return 'operator-command';
  return isProvisioningReloadReason(reason) ? 'provisioning' : 'crash';
}

function countByClass(events: readonly RestartEvent[]): Record<RestartReasonClass, number> {
  let crash = 0;
  let provisioning = 0;
  let credentialRotation = 0;
  let selfHealing = 0;
  let operatorConfig = 0;
  let scheduled = 0;
  let operatorCommand = 0;
  for (const e of events) {
    const klass = restartReasonClass(e.reason);
    if (klass === 'provisioning') provisioning += 1;
    else if (klass === 'credential-rotation') credentialRotation += 1;
    else if (klass === 'self-healing') selfHealing += 1;
    else if (klass === 'operator-config') operatorConfig += 1;
    // ENG-9138: explicit, ahead of the `else crash` fallthrough. The fallthrough
    // is what silently absorbed day-rollover into the crash tally.
    else if (klass === 'scheduled') scheduled += 1;
    // ENG-10066: explicit, for the same reason as the line above it.
    else if (klass === 'operator-command') operatorCommand += 1;
    else crash += 1;
  }
  return {
    crash,
    provisioning,
    'credential-rotation': credentialRotation,
    'self-healing': selfHealing,
    'operator-config': operatorConfig,
    scheduled,
    'operator-command': operatorCommand,
  };
}

/**
 * ENG-7812: the largest single-integration provisioning bucket among `events`.
 *
 * Provisioning-reload events are bucketed by their {@link RestartEvent.integrationKey}
 * (absent ⇒ the shared `''` unattributed bucket). Onboarding many DISTINCT
 * integrations spreads restarts across many buckets of ~1 (never trips); ONE
 * integration looping (or a run of unattributed reprovisions) piles into a
 * single bucket that crosses the bar. Returns the winning bucket's count + key
 * (count 0 / key `''` when there are no provisioning events).
 */
function maxProvisioningBucket(events: readonly RestartEvent[]): { count: number; key: string } {
  return maxBucketWhere(events, isProvisioningReloadReason);
}

/**
 * ENG-8388: the same per-integration bucketing, over an arbitrary reason class.
 * Extracted so the operator-config tally buckets identically to the provisioning
 * one — a human wiring four DIFFERENT integrations still spreads across four
 * buckets, and only a run against ONE integration piles up.
 */
function maxBucketWhere(
  events: readonly RestartEvent[],
  matches: (reason: RestartReason) => boolean,
): { count: number; key: string } {
  const buckets = new Map<string, number>();
  for (const e of events) {
    if (!matches(e.reason)) continue;
    const key = e.integrationKey ?? '';
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  let best = { count: 0, key: '' };
  for (const [key, count] of buckets) {
    if (count > best.count) best = { count, key };
  }
  return best;
}

export interface RestartEvent {
  reason: RestartReason;
  at: number;
  /**
   * ENG-7812: for a provisioning-reload event, the integration this restart is
   * attributable to (its `definition_id`), when the manager could pin one down
   * (a membership delta in the same poll window). Provisioning events are
   * TRIPPED per-integration (bucketed by this key) so onboarding many DISTINCT
   * integrations (each binding once) never trips, while ONE integration looping
   * still does. Absent when no single integration could be attributed (e.g. a
   * reprovision of an unchanged set); those share one `''` bucket that still
   * trips on its own, preserving the breaker against unattributed churn. Unused
   * for crash / credential-rotation events.
   */
  integrationKey?: string;
}

export interface TripState {
  trippedAt: number;
  eventsAtTrip: RestartEvent[];
  /** Pre-formatted `status_message` for the agents row + decision-log. */
  statusMessage: string;
  /**
   * ENG-7577: which tally tripped, persisted so the RECOVERY path can tell a
   * provisioning-reload pause (routine onboarding churn — safe to auto-recover
   * once quiet) from a crash-class pause (a structurally-stuck agent — must stay
   * paused for a human). Optional because trips persisted by a pre-ENG-7577
   * manager into `manager-state.json` have no such field; {@link tripClass}
   * reconstructs it from `eventsAtTrip` for those, fail-safe toward `crash`.
   */
  trippedClass?: RestartReasonClass;
}

/**
 * ENG-7577: the class of a trip, for hydrated trips too.
 *
 * Prefers the persisted {@link TripState.trippedClass}. For a legacy trip that
 * predates the field, reconstruct it from the event snapshot — `record()` scopes
 * `eventsAtTrip` to the tripping class alone (crash trips snapshot only crash
 * events; a provisioning trip snapshots only the overflowing integration's
 * provisioning events), so the snapshot is an exact reconstruction rather than a
 * guess. Fail-safe: an empty snapshot, or ANY crash-class event in it, resolves
 * to `crash` — the class that demands manual operator clearance. Mis-reading a
 * crash trip as provisioning would hand a genuinely broken agent an unattended
 * resume; the reverse merely costs an operator click.
 */
export function tripClass(trip: TripState): RestartReasonClass {
  if (trip.trippedClass) return trip.trippedClass;
  if (!Array.isArray(trip.eventsAtTrip) || trip.eventsAtTrip.length === 0) return 'crash';
  return trip.eventsAtTrip.every((e) => isProvisioningReloadReason(e.reason)) ? 'provisioning' : 'crash';
}

export interface RecordResult {
  /** True on the call that flips the breaker from closed → tripped. */
  tripped: boolean;
  /** Always populated when `tripped` is true. */
  trip?: TripState;
  /**
   * Current count inside the tight (`windowMs`) sliding window, ALL classes
   * (post-record). Preserved from the pre-ENG-7560 shape for callers/tests that
   * read it; the per-class breakdown lives in `classCounts`.
   */
  windowCount: number;
  /**
   * ENG-7560: per-class in-window counts at record time — `crash` inside the
   * tight window, `provisioning` inside the provisioning window. Lets the caller
   * emit an "elevated provisioning churn" signal before the looser bar trips.
   */
  classCounts: Record<RestartReasonClass, number>;
  /** ENG-7560: which class's threshold tripped (only set when `tripped`). */
  trippedClass?: RestartReasonClass;
  /**
   * ENG-7812: the largest single-integration provisioning bucket in the
   * provisioning window (post-record), and the integration key it belongs to.
   * The provisioning trip fires on THIS, not on the raw `classCounts.provisioning`
   * total, so the caller's early-warning must gate on `maxProvisioningBucket`
   * (a churning integration) rather than the total (a legitimate bulk onboard).
   * `''` key ⇒ the unattributed-provisioning bucket.
   */
  maxProvisioningBucket: number;
  maxProvisioningBucketKey?: string;
  /**
   * ENG-8690: true when a provisioning-shaped trip WOULD have fired but was
   * withheld because the agent is inside its post-creation grace.
   *
   * Deliberately not merely "the grace is active" - that would log on every
   * restart of every new agent and become noise nobody reads. This fires only on
   * the calls where the grace actually changed the outcome, which is the set an
   * operator needs to see: it is the early warning that this agent will pause
   * the moment the grace expires if the churn does not stop.
   */
  newAgentGraceSuppressedTrip?: boolean;
  /** ENG-8690: epoch-ms the grace ends. Set whenever the grace is active. */
  newAgentGraceExpiresAt?: number;
  /**
   * ENG-8690 (review): which class's bar was crossed and withheld, plus the
   * bucket that crossed it and the agent's age.
   *
   * A boolean was not enough. Suppression applies to BOTH provisioning and
   * operator-config, and the consumer was logging `maxProvisioningBucketKey`
   * unconditionally - so a suppressed operator-config trip named the wrong
   * integration, or none. The two classes have different buckets with different
   * keys, so the class has to travel with the key.
   *
   * `agentAgeMs` rides along because a suppression is only interpretable next to
   * the age: "spared at 20 minutes old" and "spared at 23 hours old" call for
   * different reactions.
   */
  newAgentGraceSuppressed?: {
    class: 'provisioning' | 'operator-config';
    bucketKey: string;
    bucketCount: number;
    agentAgeMs: number;
  };
  /**
   * ENG-8690: true when the trip fired BECAUSE the grace ceiling was exceeded -
   * i.e. the agent was inside its grace and paused anyway. Distinct from an
   * ordinary provisioning trip, which by definition happened outside the grace.
   */
  newAgentGraceCeilingExceeded?: boolean;
  /**
   * ENG-9138: set when platform-scheduled restarts crossed
   * {@link RestartBreakerOptions.scheduledMax} inside the scheduled window.
   *
   * THIS IS NOT A TRIP. `tripped` stays false and the agent keeps running — the
   * whole point of the scheduled class is that the platform's own maintenance
   * must never take a customer's agent down. It is the LOOP signal: a correct
   * day rollover fires once per agent per day, so reaching this count means the
   * rollover itself is broken. The manager reports it as a suppressed pause so a
   * human is paged about the platform bug.
   *
   * Shaped like `newAgentGraceSuppressed` (count + window + the reason that
   * dominated) because it feeds the same report: an operator reading it needs to
   * know how many, over how long, and of what.
   */
  scheduledLoop?: {
    count: number;
    windowMs: number;
    /** The most recent scheduled reason in the window — today always 'day-rollover'. */
    reason: RestartReason;
  };
  /**
   * ENG-10066: set when operator-commanded restarts crossed
   * {@link RestartBreakerOptions.operatorCommandMax} inside the operator-command
   * window.
   *
   * THIS IS NOT A TRIP, for the same reason `scheduledLoop` is not: the
   * operator-command class never pauses an agent. What it means is that a person
   * has restarted this agent many times in an hour, which says the AGENT is
   * unhealthy — not that the operator is. Before ENG-10066 that signal was
   * delivered by pausing the agent mid-recovery, which destroyed the operator's
   * ability to tell whether their last fix had worked.
   *
   * Same shape as `scheduledLoop` because it feeds the same report.
   */
  operatorCommandLoop?: {
    count: number;
    windowMs: number;
    /** The most recent operator-command reason in the window — today always 'channel-restart-flag'. */
    reason: RestartReason;
  };
}

/**
 * ENG-8690 (review): the largest grace we will honour. See the clamp in the
 * constructor - an unbounded duration overflows the Date range and makes the
 * manager throw while formatting the expiry.
 */
const MAX_NEW_AGENT_GRACE_MS = 365 * 24 * 60 * 60 * 1000;

const DEFAULT_MAX = 2;
const DEFAULT_WINDOW_MS = 600_000;
// ENG-7560: provisioning-reload churn (a burst of hot-reload / bind-remediation
// restarts from one integration-add) gets its own looser tally so a routine add
// can't trip the tight crash breaker, while sustained provisioning thrash still
// trips and pages. 5-in-30min clears a single add's 3-4 burst but a flap
// re-firing more often than ~every 6 min still accumulates to a trip.
// ENG-8215: sourced from @augmented/core so the MCP quarantine threshold can be
// DERIVED from the same number. Tuning the breaker here now moves quarantine
// with it, instead of silently re-opening the race where the breaker always
// tripped before quarantine could isolate the failing integration.
const DEFAULT_PROVISIONING_MAX = RESTART_BREAKER_PROVISIONING_MAX;
const DEFAULT_PROVISIONING_WINDOW_MS = RESTART_BREAKER_PROVISIONING_WINDOW_MS;
const DEFAULT_OPERATOR_MAX = RESTART_BREAKER_OPERATOR_MAX;
// ENG-9138: the scheduled class's LOOP bar (not a trip bar) and its window.
const DEFAULT_SCHEDULED_MAX = RESTART_BREAKER_SCHEDULED_MAX;
const DEFAULT_SCHEDULED_WINDOW_MS = RESTART_BREAKER_SCHEDULED_WINDOW_MS;
const DEFAULT_OPERATOR_COMMAND_MAX = RESTART_BREAKER_OPERATOR_COMMAND_MAX;
const DEFAULT_OPERATOR_COMMAND_WINDOW_MS = RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS;

export function readEnvNumber(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * ENG-7891 slice 5b: move a map entry from the legacy code_name key onto the
 * rename-stable runtime key on first touch after the id-keyed layout is armed.
 * No-op when the target already holds an entry or the source is absent, so it is
 * safe to call on every access. Never invoked on the legacy fleet (keyFor only
 * calls it when from !== to, and identity makes from === to).
 */
function migrateMapKey<V>(map: Map<string, V>, from: string, to: string): void {
  if (map.has(to) || !map.has(from)) return;
  map.set(to, map.get(from)!);
  map.delete(from);
}

/**
 * Per-agent breaker. One instance per manager, keyed internally by code_name.
 * Held in module-level state in manager-worker.ts so it survives across
 * polls within a worker generation; trip state is mirrored to
 * `manager-state.json` so it survives across worker generations too.
 */
export class RestartBreaker {
  private readonly max: number;
  private readonly windowMs: number;
  /**
   * ENG-8215: NOT readonly - the constructor may clamp it upward to preserve the
   * quarantine-fires-first ordering against a runtime override. Never mutated
   * after construction.
   */
  private provisioningMax: number;
  private readonly provisioningWindowMs: number;
  /**
   * ENG-8388: the looser operator-config budget. Shares provisioningWindowMs.
   * NOT readonly - the constructor floors it at `provisioningMax` so setting one
   * env var without the other cannot invert the two tallies.
   */
  private operatorMax: number;
  /**
   * ENG-9138: the scheduled class's LOOP bar. Readonly and deliberately NOT
   * floored against any other tally: it gates a REPORT, not a pause, so it
   * cannot invert anything the way operatorMax/provisioningMax can.
   */
  private readonly scheduledMax: number;
  /** ENG-9138: sliding window for the scheduled tally. */
  private readonly scheduledWindowMs: number;
  private readonly operatorCommandMax: number;
  private readonly operatorCommandWindowMs: number;
  /** Longest window any tally needs — how long the events log must retain. */
  private readonly retentionMs: number;
  private readonly now: () => number;
  /** ENG-7891 slice 5b: code_name → durable runtime key (agent_id when id-keyed). Identity on the legacy fleet. */
  private readonly runtimeKey: (codeName: string) => string;
  /** ENG-8690: agent creation time lookup; undefined result ⇒ no grace. */
  private readonly agentCreatedAt: (codeName: string) => number | undefined;
  /** ENG-8690: grace duration; 0 disables. */
  private readonly newAgentGraceMs: number;
  /** ENG-8690: the bound the grace does NOT lift. NOT readonly - floored below. */
  private newAgentGraceCeiling: number;
  private readonly events = new Map<string, RestartEvent[]>();
  private readonly trips = new Map<string, TripState>();
  /**
   * ENG-8215: set when a supplied `provisioningMax` was raised to keep MCP
   * quarantine ahead of the breaker. Read once by the manager at construction so
   * an overridden-and-clamped value is visible in manager.log rather than
   * silently discarded.
   */
  private quarantineOrderingClamped?: {
    requested: number;
    applied: number;
    quarantineThreshold: number;
  };

  constructor(opts: RestartBreakerOptions = {}) {
    this.max = opts.max ?? readEnvNumber('AGT_RESTART_BREAKER_MAX', DEFAULT_MAX);
    this.windowMs = opts.windowMs ?? readEnvNumber('AGT_RESTART_BREAKER_WINDOW_MS', DEFAULT_WINDOW_MS);
    this.provisioningMax =
      opts.provisioningMax ?? readEnvNumber('AGT_RESTART_BREAKER_PROVISIONING_MAX', DEFAULT_PROVISIONING_MAX);
    this.provisioningWindowMs =
      opts.provisioningWindowMs ??
      readEnvNumber('AGT_RESTART_BREAKER_PROVISIONING_WINDOW_MS', DEFAULT_PROVISIONING_WINDOW_MS);
    this.operatorMax =
      opts.operatorMax ?? readEnvNumber('AGT_RESTART_BREAKER_OPERATOR_MAX', DEFAULT_OPERATOR_MAX);
    // ENG-9138: readEnvNumber already rejects non-finite and non-positive values
    // by falling back to the default, so a fat-fingered env var cannot produce a
    // bar of 0 that reports a loop on the first scheduled restart of the day.
    this.scheduledMax =
      opts.scheduledMax ?? readEnvNumber('AGT_RESTART_BREAKER_SCHEDULED_MAX', DEFAULT_SCHEDULED_MAX);
    this.scheduledWindowMs =
      opts.scheduledWindowMs ??
      readEnvNumber('AGT_RESTART_BREAKER_SCHEDULED_WINDOW_MS', DEFAULT_SCHEDULED_WINDOW_MS);
    this.operatorCommandMax =
      opts.operatorCommandMax ??
      readEnvNumber('AGT_RESTART_BREAKER_OPERATOR_COMMAND_MAX', DEFAULT_OPERATOR_COMMAND_MAX);
    this.operatorCommandWindowMs =
      opts.operatorCommandWindowMs ??
      readEnvNumber(
        'AGT_RESTART_BREAKER_OPERATOR_COMMAND_WINDOW_MS',
        DEFAULT_OPERATOR_COMMAND_WINDOW_MS,
      );
    this.now = opts.now ?? Date.now;
    this.runtimeKey = opts.runtimeKey ?? ((codeName) => codeName);
    // ENG-8690: no lookup supplied ⇒ nobody is ever in grace. See the option doc:
    // unknown age must mean "governed", never "exempt".
    this.agentCreatedAt = opts.agentCreatedAt ?? (() => undefined);
    const graceMs =
      opts.newAgentGraceMs ??
      readEnvNumber('AGT_RESTART_BREAKER_NEW_AGENT_GRACE_MS', RESTART_BREAKER_NEW_AGENT_GRACE_MS);
    // A non-finite or negative override disables the grace rather than throwing.
    // Same reasoning as the ENG-8215 clamp: refusing to boot on a fat-fingered
    // env var is worse than running with the SAFER behaviour, and here the safer
    // behaviour is the pre-ENG-8690 one (no grace, breaker fully armed).
    // ENG-8690 (review): CLAMP, do not just validate. A finite positive duration
    // is not sufficient - `createdAt + newAgentGraceMs` can exceed the ECMAScript
    // Date range (8.64e15), and the manager then throws
    // `RangeError: Invalid time value` from `toISOString()` while formatting the
    // expiry. That would crash the RESTART path itself, turning a fat-fingered
    // env var into an agent that cannot be restarted at all - strictly worse than
    // anything the grace prevents.
    //
    // Capped at a year rather than at the Date bound: a grace measured in
    // millennia is a typo, not an intent, and clamping to something meaningful
    // beats clamping to something merely representable.
    const cappedGrace = Math.min(graceMs, MAX_NEW_AGENT_GRACE_MS);
    this.newAgentGraceMs = Number.isFinite(graceMs) && graceMs > 0 ? cappedGrace : 0;
    const ceiling =
      opts.newAgentGraceCeiling ??
      readEnvNumber(
        'AGT_RESTART_BREAKER_NEW_AGENT_GRACE_CEILING',
        RESTART_BREAKER_NEW_AGENT_GRACE_CEILING,
      );
    this.newAgentGraceCeiling =
      Number.isFinite(ceiling) && ceiling > 0 ? ceiling : RESTART_BREAKER_NEW_AGENT_GRACE_CEILING;
    // Reject NaN / Infinity explicitly — `??` treats them as present
    // values, so without these guards an operator who sets
    // `AGT_RESTART_BREAKER_MAX=NaN` (or a programmatic caller passing
    // `Number.NaN`) would silently disable the breaker rather than
    // fall through to the default. CodeRabbit on PR #1302.
    if (!Number.isFinite(this.max) || this.max < 1) {
      throw new Error('restart-breaker max must be a finite number >= 1');
    }
    if (!Number.isFinite(this.windowMs) || this.windowMs < 1000) {
      throw new Error('restart-breaker windowMs must be a finite number >= 1000');
    }
    // ENG-7560: guard the provisioning tally with the same shape so a
    // fat-fingered AGT_RESTART_BREAKER_PROVISIONING_* can't silently disable it.
    if (!Number.isFinite(this.provisioningMax) || this.provisioningMax < 1) {
      throw new Error('restart-breaker provisioningMax must be a finite number >= 1');
    }
    // ENG-8388: same shape for the operator tally. Floored at the provisioning
    // max rather than at 1 — an operator budget BELOW the autonomous one would
    // invert the whole point of the class (proving a human asked would make the
    // agent MORE likely to be paused, not less), and that inversion is easy to
    // reach by setting only one of the two env vars.
    if (!Number.isFinite(this.operatorMax) || this.operatorMax < 1) {
      throw new Error('restart-breaker operatorMax must be a finite number >= 1');
    }
    if (this.operatorMax < this.provisioningMax) {
      this.operatorMax = this.provisioningMax;
    }
    // ENG-8215 (CodeRabbit on this PR): coordinating the DEFAULTS is not enough.
    // `AGT_RESTART_BREAKER_PROVISIONING_MAX` and `opts.provisioningMax` can each
    // lower the breaker's bar on their own, and the quarantine threshold is
    // enforced server-side where a host's env is invisible - so an operator
    // setting `2` would trip the breaker on the 3rd provisioning restart while
    // quarantine still waited for its 3rd bind failure, silently re-opening the
    // exact race this ticket closes. The ordering therefore has to be enforced
    // HERE, on the only side that can see both numbers.
    //
    // Clamp rather than throw: a manager that refuses to boot on a fat-fingered
    // env var is a worse outcome than one running with a slightly looser
    // breaker. The breaker is the safety net; quarantine-fires-first is the
    // invariant. `quarantineOrderingClamped` records that we overrode the
    // operator, so the caller can log it rather than discard it silently.
    if (this.provisioningMax < MIN_PROVISIONING_MAX_FOR_QUARANTINE_ORDERING) {
      this.quarantineOrderingClamped = {
        requested: this.provisioningMax,
        applied: MIN_PROVISIONING_MAX_FOR_QUARANTINE_ORDERING,
        quarantineThreshold: BIND_FAILURE_QUARANTINE_THRESHOLD,
      };
      this.provisioningMax = MIN_PROVISIONING_MAX_FOR_QUARANTINE_ORDERING;
    }
    if (!Number.isFinite(this.provisioningWindowMs) || this.provisioningWindowMs < 1000) {
      throw new Error('restart-breaker provisioningWindowMs must be a finite number >= 1000');
    }
    // ENG-10066 (CodeRabbit on PR #5709): validate the two NON-tripping tallies
    // with the same shape as the tripping ones. `readEnvNumber` already floors
    // the env path — it falls back to the default on anything non-finite or
    // non-positive — but `opts.scheduledWindowMs` and `opts.operatorCommandWindowMs`
    // reach the field directly through the `??`, and BOTH feed `retentionMs` via
    // the Math.max below. `operatorCommandWindowMs: Infinity` therefore makes
    // retention infinite and the event log never prunes: an unbounded Map in a
    // process that runs for weeks.
    //
    // The comment eleven lines above ("readEnvNumber already rejects non-finite
    // and non-positive values") was written about the scheduled class in ENG-9138
    // and is true only of the env path. It read as though the field were
    // protected. It was not, and neither was mine — so the scheduled pair is
    // validated here too rather than left as the same hole one class along.
    if (!Number.isFinite(this.scheduledMax) || this.scheduledMax < 1) {
      throw new Error('restart-breaker scheduledMax must be a finite number >= 1');
    }
    if (!Number.isFinite(this.scheduledWindowMs) || this.scheduledWindowMs < 1000) {
      throw new Error('restart-breaker scheduledWindowMs must be a finite number >= 1000');
    }
    if (!Number.isFinite(this.operatorCommandMax) || this.operatorCommandMax < 1) {
      throw new Error('restart-breaker operatorCommandMax must be a finite number >= 1');
    }
    if (!Number.isFinite(this.operatorCommandWindowMs) || this.operatorCommandWindowMs < 1000) {
      throw new Error('restart-breaker operatorCommandWindowMs must be a finite number >= 1000');
    }
    // ENG-8690: floor AFTER provisioningMax is final (the quarantine-ordering
    // clamp above can raise it). A grace ceiling below the normal bar would pause
    // a NEW agent sooner than a mature one - the exact inversion the grace exists
    // to prevent.
    //
    // FLOORED AT BOTH BARS, not just the provisioning one (CodeRabbit, #4321).
    // The ceiling counts provisioning AND operator-config events together, but
    // the two classes have SEPARATE thresholds and `operatorMax` is the looser
    // of the pair by default - so flooring only at `provisioningMax` left the
    // inversion fully intact for the other class. With `operatorMax: 50` and a
    // ceiling of 30, a brand-new agent paused on its 31st operator-config
    // restart while a mature one survived to 51. Same defect this floor was
    // added to fix, one class along, which is where a partial fix hides.
    const minimumGraceCeiling = Math.max(this.provisioningMax, this.operatorMax);
    if (this.newAgentGraceCeiling < minimumGraceCeiling) {
      this.newAgentGraceCeiling = minimumGraceCeiling;
    }
    // ENG-9138: the scheduled window (60 min by default) is the LONGEST of the
    // three, so it has to join this max. Leaving it out would prune scheduled
    // events out of the log before the scheduled tally could count them, and the
    // loop signal would silently never fire — a dead detector that still reports
    // as armed, which is the failure mode this ticket exists to stop.
    // ENG-10066: the operator-command window joins the max. Retention shorter
    // than a tally's window would silently prune the events that tally counts.
    this.retentionMs = Math.max(
      this.windowMs,
      this.provisioningWindowMs,
      this.scheduledWindowMs,
      this.operatorCommandWindowMs,
    );
  }

  /**
   * ENG-8215: the clamp record, if the configured provisioning max had to be
   * raised to keep MCP quarantine ahead of the breaker. `undefined` when the
   * configured value already satisfied the ordering.
   */
  getQuarantineOrderingClamp(): { requested: number; applied: number; quarantineThreshold: number } | undefined {
    return this.quarantineOrderingClamped;
  }

  /**
   * ENG-7891 slice 5b: the DURABLE key for this agent's events/trips entries.
   * Resolves the code_name to the runtime key (agent_id on id-keyed hosts) and,
   * when they differ, lazily migrates a legacy code_name-keyed entry onto it — so
   * a trip persisted under the old key by a pre-arming manager (or from before a
   * codename rename) is found under the new key instead of being lost. Identity on
   * the legacy fleet ⇒ from === to ⇒ no migration and no behaviour change. Both
   * maps migrate together so events and a trip for the same agent never split
   * across keys mid-generation (a migration can flip the resolver mid-process).
   *
   * BOUNDARY — a pure-LEGACY rename (a host that was never armed) still strands a
   * held trip, exactly as it did before slice 5b: with no symlink there is no
   * agent_id to bridge the old codename to the new, so the runtime key stays the
   * (new) codename and the trip persisted under the old codename is not found.
   * That is unchanged, pre-existing behaviour and it is benign — the fresh
   * generation re-evaluates the agent under its new name and a still-pathological
   * agent simply re-trips within the window. The rename-survival guarantee this
   * slice adds is an id-keyed-only, post-arming property (the symlink is the
   * bridge); it is deliberately NOT retrofitted onto the legacy path.
   */
  private keyFor(codeName: string): string {
    const key = this.runtimeKey(codeName);
    if (key !== codeName) {
      migrateMapKey(this.events, codeName, key);
      migrateMapKey(this.trips, codeName, key);
    }
    return key;
  }

  /** True if this agent's breaker is currently tripped (manager must skip spawn). */
  isTripped(codeName: string): boolean {
    return this.trips.has(this.keyFor(codeName));
  }

  getTrip(codeName: string): TripState | undefined {
    return this.trips.get(this.keyFor(codeName));
  }

  /**
   * Record a restart event. If recording this event puts the count
   * inside the window above `max`, the breaker trips and the call site
   * should NOT respawn.
   *
   * Idempotent on already-tripped breakers: returns the existing trip
   * without double-counting events. Callers may still record the reason
   * via the decision-log for forensics.
   */
  record(codeName: string, reason: RestartReason, integrationKey?: string): RecordResult {
    const key = this.keyFor(codeName);
    const existing = this.trips.get(key);
    if (existing) {
      const bucket = maxProvisioningBucket(existing.eventsAtTrip);
      return {
        tripped: false,
        trip: existing,
        windowCount: existing.eventsAtTrip.length,
        classCounts: countByClass(existing.eventsAtTrip),
        maxProvisioningBucket: bucket.count,
        maxProvisioningBucketKey: bucket.key,
      };
    }

    const at = this.now();
    // Retain for the longer of the two windows so the provisioning tally can see
    // events older than the tight crash window. Per-class counting below applies
    // each class's own (shorter) window on top of this retained log.
    const retentionCutoff = at - this.retentionMs;
    const prior = (this.events.get(key) ?? []).filter((e) => e.at >= retentionCutoff);
    // ENG-7812: pin the integration key only onto provisioning-reload events (it
    // buckets the provisioning trip). Crash / credential-rotation events keep it
    // undefined so a stray caller key can't perturb their tallies.
    // ENG-8388: operator-config events are bucketed the same way, so they need
    // the key pinned too - otherwise every operator reload would share the ''
    // bucket and four distinct integration adds would count as one run.
    // The predicate is shared with the manager's caller (which resolves the key
    // this pins) so the two halves of the contract cannot drift apart again -
    // they did, and it made this bucketing unreachable. See
    // isIntegrationBucketedReason.
    const bucketed = isIntegrationBucketedReason(reason);
    prior.push(bucketed && integrationKey ? { reason, at, integrationKey } : { reason, at });
    this.events.set(key, prior);

    // ENG-7560: reason-aware, two independent tallies. Crash reasons keep the
    // tight ENG-5441 bar; provisioning-reload churn gets its own looser bar.
    // Every event is still recorded (forensics + the elevated-churn signal the
    // caller emits) — we relax the TRIP threshold, we do not skip/mute records,
    // so a slow flap can never space recorded events past its window and quietly
    // defeat the breaker.
    const crashCount = prior.filter(
      (e) => e.at >= at - this.windowMs && restartReasonClass(e.reason) === 'crash',
    ).length;
    const provEvents = prior.filter(
      (e) => e.at >= at - this.provisioningWindowMs && isProvisioningReloadReason(e.reason),
    );
    // ENG-7541: credential-rotation re-mints are RECORDED and surfaced (so the
    // un-paused respawn loop is visible in classCounts / logs) but never
    // contribute to a trip. Counted over the provisioning window so
    // "re-mints in the last 30 min" is a meaningful operator number. There is
    // deliberately NO trip check for this count below.
    const credRotCount = prior.filter(
      (e) => e.at >= at - this.provisioningWindowMs && isCredentialRotationReason(e.reason),
    ).length;
    // ENG-8215: self-healing restarts (bind-remediation) are RECORDED and
    // surfaced for exactly the same reason as credential-rotation above - the
    // repair loop must stay visible - but there is deliberately NO trip check
    // for this count either. A repair attempt is not evidence of ill health.
    const selfHealCount = prior.filter(
      (e) => e.at >= at - this.provisioningWindowMs && isSelfHealingReason(e.reason),
    ).length;
    // ENG-8388: provisioning reloads a console user demonstrably asked for.
    // RECORDED and surfaced like every other class, but tallied against its own
    // looser bar below rather than against `provisioningMax`.
    const operatorEvents = prior.filter(
      (e) => e.at >= at - this.provisioningWindowMs && isOperatorConfigReason(e.reason),
    );
    // ENG-9138: platform-scheduled restarts. RECORDED and surfaced like every
    // other class, and — like credential-rotation and self-healing above — there
    // is deliberately NO trip check for this count. What it does feed is the
    // `scheduledLoop` REPORT below: a bar that pages a human without taking the
    // agent down. Counted over its own (longest) window.
    const scheduledEvents = prior.filter(
      (e) => e.at >= at - this.scheduledWindowMs && isScheduledRestartReason(e.reason),
    );
    // ENG-10066: operator-commanded restarts. RECORDED and surfaced like every
    // other class, and — as with scheduled above — there is deliberately NO trip
    // check for this count. It feeds the `operatorCommandLoop` REPORT below.
    const operatorCommandEvents = prior.filter(
      (e) => e.at >= at - this.operatorCommandWindowMs && isOperatorCommandReason(e.reason),
    );
    // The back-compat `windowCount`: all events in the tight window, unchanged.
    const windowCount = prior.filter((e) => e.at >= at - this.windowMs).length;
    const classCounts: Record<RestartReasonClass, number> = {
      crash: crashCount,
      provisioning: provEvents.length,
      'credential-rotation': credRotCount,
      'self-healing': selfHealCount,
      'operator-config': operatorEvents.length,
      scheduled: scheduledEvents.length,
      'operator-command': operatorCommandEvents.length,
    };
    // ENG-9138: the loop signal. Computed once, attached to BOTH return paths
    // below — a scheduled loop that coincides with a genuine crash trip must
    // still be reported, or the rollover bug hides behind the pause it did not
    // cause. `scheduledEvents` is filtered from `prior` (append-ordered), so the
    // last element is the most recent scheduled event, not merely some member.
    const scheduledLoop =
      scheduledEvents.length > this.scheduledMax
        ? {
            count: scheduledEvents.length,
            windowMs: this.scheduledWindowMs,
            reason: scheduledEvents[scheduledEvents.length - 1]!.reason,
          }
        : undefined;
    // ENG-10066: the operator-command loop signal, computed and attached exactly
    // like `scheduledLoop` and for the same reason — a run of operator restarts
    // that coincides with a genuine crash trip must still be reported, or the
    // "somebody has been restarting this agent all hour" signal hides behind the
    // pause it did not cause.
    const operatorCommandLoop =
      operatorCommandEvents.length > this.operatorCommandMax
        ? {
            count: operatorCommandEvents.length,
            windowMs: this.operatorCommandWindowMs,
            reason: operatorCommandEvents[operatorCommandEvents.length - 1]!.reason,
          }
        : undefined;
    // ENG-7812: the provisioning trip fires PER-INTEGRATION, on the largest
    // single-integration bucket, not on `provEvents.length`. A bulk onboard of N
    // distinct integrations spreads N restarts across N buckets of ~1 (max bucket
    // stays small, no trip); ONE integration looping (or a run of unattributed
    // reprovisions sharing the `''` bucket) piles into a single bucket that
    // crosses `provisioningMax`. This is what stops a healthy new agent from
    // auto-pausing mid-onboarding while still catching a genuinely churning add.
    const provBucket = maxProvisioningBucket(provEvents);
    // ENG-8388: the operator tally buckets identically, so wiring four DIFFERENT
    // integrations spreads across four buckets and never approaches the bar,
    // while a genuine run against ONE integration still has a backstop.
    const operatorBucket = maxBucketWhere(operatorEvents, isOperatorConfigReason);

    // ENG-8690: is this agent inside its post-creation grace?
    //
    // Computed here, used ONLY to gate the two provisioning-shaped branches
    // below. Everything above this line - the recording, every class count, the
    // buckets - is untouched, exactly as ENG-7541 and ENG-8215 treat their
    // non-tripping classes: a suppressed trip must still be a visible event.
    //
    // `age >= 0` matters. A created_at in the FUTURE (clock skew on the host, or
    // a bad row) yields a negative age, which a naive `age < graceMs` would read
    // as "in grace" and hand that agent a permanent exemption. Requiring a
    // non-negative age fails toward the breaker being armed.
    const createdAt = this.newAgentGraceMs > 0 ? this.agentCreatedAt(codeName) : undefined;
    const age = typeof createdAt === 'number' && Number.isFinite(createdAt) ? at - createdAt : undefined;
    const newAgentGraceActive = age !== undefined && age >= 0 && age < this.newAgentGraceMs;
    const newAgentGraceExpiresAt =
      newAgentGraceActive && typeof createdAt === 'number' ? createdAt + this.newAgentGraceMs : undefined;
    // ENG-8690: the bound the grace does not lift. Both provisioning-shaped
    // classes together - an agent alternating between them is still thrashing.
    //
    // FILTERED FROM `prior`, NOT CONCATENATED FROM THE TWO CLASS ARRAYS
    // (CodeRabbit, #4321). `[...provEvents, ...operatorEvents]` has the same
    // MEMBERS but not the same ORDER: every provisioning event sorts before
    // every operator event regardless of when each happened.
    // `formatStatusMessage` reports `events[events.length - 1]` as "most
    // recent", so a concatenated array names the last OPERATOR event even when
    // a provisioning restart happened after it - a status line stating a wrong
    // time and a wrong reason for the thing that just paused the agent.
    //
    // `Array.prototype.filter` preserves source order, and `prior` is the
    // append-ordered event log, so this is chronological by construction - the
    // same way every sibling branch below derives its `tripEvents`. No explicit
    // sort, deliberately: it would be the only branch that distrusts that
    // ordering, which reads as though the others are unsafe.
    const graceCeilingEvents = newAgentGraceActive
      ? prior.filter(
          (e) =>
            e.at >= at - this.provisioningWindowMs &&
            (isProvisioningReloadReason(e.reason) || isOperatorConfigReason(e.reason)),
        )
      : [];
    const graceCeilingExceeded =
      newAgentGraceActive && graceCeilingEvents.length > this.newAgentGraceCeiling;

    // Crash bar is the more urgent classification — evaluate it first so a mixed
    // burst that crosses BOTH reports as a crash trip.
    let trippedClass: RestartReasonClass | undefined;
    let tripEvents: RestartEvent[] | undefined;
    let tripWindowMs = this.windowMs;
    // ENG-8388: which bucket key the status message should name. The two bucketed
    // classes have DIFFERENT keys, and passing provBucket.key unconditionally
    // (as this did before the operator class existed) makes an operator trip name
    // the wrong integration - or none at all, when only operator events exist.
    let tripBucketKey = '';
    if (crashCount > this.max) {
      trippedClass = 'crash';
      tripEvents = prior.filter(
        (e) => e.at >= at - this.windowMs && restartReasonClass(e.reason) === 'crash',
      );
      tripWindowMs = this.windowMs;
    } else if (newAgentGraceActive && graceCeilingExceeded) {
      // ENG-8690: the grace is in force and was NOT enough. Counted across ALL
      // buckets rather than per-integration: during onboarding "one integration
      // is churning" is expected, so the question the ceiling asks is the
      // different one - is this agent doing anything except restarting.
      //
      // Reported as a provisioning trip (it is one) but with a status message
      // that names the ceiling, because "paused despite the new-agent grace" is
      // a materially different thing for an operator to investigate than an
      // ordinary provisioning pause.
      trippedClass = 'provisioning';
      tripEvents = graceCeilingEvents;
      tripWindowMs = this.provisioningWindowMs;
      // NO BUCKET KEY (CodeRabbit, #4321). A ceiling trip is aggregate by
      // definition - the comment two lines up says "counted across ALL buckets"
      // - so naming `provBucket.key` would label the whole cross-class total
      // with whichever single integration happened to have the biggest
      // provisioning bucket. An operator reads "for integration 'gmail'" and
      // goes to investigate gmail, when the actual finding is that the agent is
      // restarting for many reasons at once. The class label is dropped for the
      // same reason at the message site below: "provisioning-reload restarts"
      // is false when operator-config events are in the total.
      tripBucketKey = '';
    } else if (newAgentGraceActive) {
      // ENG-8690: inside the grace, provisioning-shaped churn cannot pause the
      // agent. Deliberately placed AFTER the crash branch so a crash-looping new
      // agent still trips on the tight bar - "it is new" predicts config churn,
      // not segfaults, and 24h of unattended respawns is the failure this whole
      // subsystem exists to stop.
      //
      // No `trippedClass` is set, so this falls through to the untripped return
      // with full `classCounts` and `maxProvisioningBucket` intact: the churn
      // stays completely visible to the caller's elevated-churn signal and to
      // forensics. Only the pause is withheld.
    } else if (operatorBucket.count > this.operatorMax) {
      // ENG-8388: a human-attributed run against ONE integration that is still
      // going after `operatorMax` reloads. Evaluated BEFORE the provisioning bar
      // only in the sense that it has its own bucket set - the two tallies are
      // disjoint by construction (a reason is in exactly one class), so the
      // order between these two branches never decides which fires.
      trippedClass = 'operator-config';
      tripEvents = operatorEvents.filter((e) => (e.integrationKey ?? '') === operatorBucket.key);
      tripWindowMs = this.provisioningWindowMs;
      tripBucketKey = operatorBucket.key;
    } else if (provBucket.count > this.provisioningMax) {
      trippedClass = 'provisioning';
      // Snapshot ONLY the overflowing integration's events so the status message
      // names the culprit ("... integration-change=6 [gmail]"), not the whole
      // multi-integration onboard burst that happened to share the window.
      tripEvents = provEvents.filter((e) => (e.integrationKey ?? '') === provBucket.key);
      tripWindowMs = this.provisioningWindowMs;
      tripBucketKey = provBucket.key;
    }

    if (trippedClass && tripEvents) {
      // ENG-8690: a ceiling trip is still a provisioning trip - same class, same
      // recovery semantics - but the operator-facing message has to say that the
      // new-agent grace WAS in force and was not enough. Without that, this is
      // indistinguishable from an ordinary provisioning pause and the reader
      // draws the wrong conclusion about a brand-new agent.
      // A ceiling trip gets the UNLABELLED message: no integration, no
      // class prefix. `trippedClass` stays 'provisioning' because the recovery
      // semantics are a provisioning trip's - but the operator-facing sentence
      // must not claim a single integration or a single class caused a total
      // that spans both. The ceiling-specific suffix below says what actually
      // happened.
      const baseMessage = graceCeilingExceeded
        ? formatStatusMessage(tripEvents, tripWindowMs)
        : formatStatusMessage(tripEvents, tripWindowMs, trippedClass, tripBucketKey);
      // ENG-8690 (review): the text must describe the data it was computed from.
      // It previously hardcoded "under 24h old", which is false whenever
      // `newAgentGraceMs` is tuned, and said "provisioning restarts" when
      // operator-config events count toward the ceiling too.
      const graceHours = Math.round((this.newAgentGraceMs / 3_600_000) * 10) / 10;
      const statusMessage = graceCeilingExceeded
        ? `${baseMessage} — paused DESPITE the new-agent grace: ${graceCeilingEvents.length} ` +
          `provisioning-shaped restarts (provisioning + operator-config) exceeded the grace ` +
          `ceiling of ${this.newAgentGraceCeiling} (ENG-8690). The agent is inside its ` +
          `${graceHours}h grace and is doing little except restarting.`
        : baseMessage;
      const trip: TripState = {
        trippedAt: at,
        eventsAtTrip: [...tripEvents],
        statusMessage,
        // ENG-7577: persist the tripping class so the recovery path can gate on
        // it after a manager restart, without re-deriving it from prose.
        trippedClass,
      };
      this.trips.set(key, trip);
      // Free the events log — the trip carries the snapshot from now on.
      this.events.delete(key);
      return {
        tripped: true,
        trip,
        windowCount,
        classCounts,
        trippedClass,
        maxProvisioningBucket: provBucket.count,
        maxProvisioningBucketKey: provBucket.key,
        ...(graceCeilingExceeded ? { newAgentGraceCeilingExceeded: true } : {}),
        // ENG-9138: a co-occurring rollover loop still reports on a real trip.
        ...(scheduledLoop ? { scheduledLoop } : {}),
        ...(operatorCommandLoop ? { operatorCommandLoop } : {}),
      };
    }

    // ENG-8690: report the grace only when it CHANGED the outcome - i.e. a
    // provisioning-shaped bar was actually crossed and the pause was withheld.
    // Recomputed from the same buckets the branches above used, rather than set
    // inside the branch, so it stays correct if a future class is added.
    // ENG-8690: which bar was actually crossed decides which bucket describes it.
    // Operator-config is checked FIRST to mirror the trip-branch order below the
    // grace, so a suppression reports the same class the pause would have.
    const suppressedOperator = operatorBucket.count > this.operatorMax;
    const suppressedProvisioning = !suppressedOperator && provBucket.count > this.provisioningMax;
    const wouldHaveTripped = suppressedOperator || suppressedProvisioning;
    return {
      tripped: false,
      windowCount,
      classCounts,
      maxProvisioningBucket: provBucket.count,
      maxProvisioningBucketKey: provBucket.key,
      ...(scheduledLoop ? { scheduledLoop } : {}),
      ...(operatorCommandLoop ? { operatorCommandLoop } : {}),
      ...(newAgentGraceActive
        ? {
            newAgentGraceSuppressedTrip: wouldHaveTripped,
            ...(newAgentGraceExpiresAt !== undefined
              ? { newAgentGraceExpiresAt }
              : {}),
            ...(wouldHaveTripped
              ? {
                  newAgentGraceSuppressed: {
                    class: suppressedOperator
                      ? ('operator-config' as const)
                      : ('provisioning' as const),
                    bucketKey: suppressedOperator ? operatorBucket.key : provBucket.key,
                    bucketCount: suppressedOperator ? operatorBucket.count : provBucket.count,
                    agentAgeMs: age ?? 0,
                  },
                }
              : {}),
          }
        : {}),
    };
  }

  /** Operator-initiated reset: drops the trip + the events log for this agent. */
  clear(codeName: string): void {
    const key = this.keyFor(codeName);
    this.trips.delete(key);
    this.events.delete(key);
    // keyFor's migration leaves a legacy code_name entry behind only when the
    // runtime key was already populated (both keys present); drop it too so an
    // operator clear leaves nothing tripped under either key.
    if (key !== codeName) {
      this.trips.delete(codeName);
      this.events.delete(codeName);
    }
  }

  /** Snapshot tripped agents for `manager-state.json`. */
  serialize(): Record<string, TripState> {
    return Object.fromEntries(this.trips.entries());
  }

  /**
   * Rehydrate trip state from `manager-state.json`. Called once at
   * worker startup. Window history is intentionally NOT persisted — only
   * tripped state. A tripped breaker survives manager restart; an
   * un-tripped one starts a fresh window in the new worker.
   */
  hydrate(saved: Record<string, TripState> | null | undefined): void {
    if (!saved) return;
    for (const [codeName, trip] of Object.entries(saved)) {
      if (trip && typeof trip.trippedAt === 'number' && Array.isArray(trip.eventsAtTrip)) {
        // ENG-7577: a persisted `trippedClass` governs whether this trip may
        // auto-recover, so an unrecognised value must not survive rehydration —
        // drop it and let `tripClass` reconstruct from the (fail-safe) event
        // snapshot rather than trusting a garbled state file.
        const klass = trip.trippedClass;
        this.trips.set(
          codeName,
          klass === undefined || KNOWN_REASON_CLASSES.has(klass) ? trip : { ...trip, trippedClass: undefined },
        );
      }
    }
  }

  /** Test helper — current in-window event count for `codeName`. */
  windowCount(codeName: string): number {
    const cutoff = this.now() - this.windowMs;
    return (this.events.get(this.keyFor(codeName)) ?? []).filter((e) => e.at >= cutoff).length;
  }
}

function formatStatusMessage(
  events: RestartEvent[],
  windowMs: number,
  klass: RestartReasonClass = 'crash',
  integrationKey = '',
): string {
  const last = events[events.length - 1]!;
  // Render the window in seconds for sub-minute thresholds (operator-
  // tuned via AGT_RESTART_BREAKER_WINDOW_MS) and in decimal minutes
  // otherwise. Math.round(/60_000) was returning "0min" for a 30s
  // tuning, which is unhelpful in the dashboard. CodeRabbit on PR #1302.
  const windowLabel = windowMs < 60_000
    ? `${Math.round(windowMs / 1000)}s`
    : `${(windowMs / 60_000).toFixed(1).replace(/\.0$/, '')}min`;
  const reasonCounts = new Map<RestartReason, number>();
  for (const e of events) reasonCounts.set(e.reason, (reasonCounts.get(e.reason) ?? 0) + 1);
  const breakdown = Array.from(reasonCounts.entries())
    .map(([r, n]) => `${r}=${n}`)
    .join(', ');
  // ENG-7560: name the tally that tripped so the operator sees "provisioning-reload
  // thrash" (a churning integration) vs a genuine crash loop at a glance.
  // ENG-8388: an operator-config trip is a DIFFERENT operator story - it means a
  // human kept re-applying config against one integration well past the point
  // the platform expected, so the message must not read as autonomous thrash.
  const classLabel =
    klass === 'provisioning'
      ? 'provisioning-reload '
      : klass === 'operator-config'
        ? 'operator-requested config-reload '
        : '';
  // ENG-7812: on a provisioning trip, name the single integration whose bucket
  // overflowed (the events are already scoped to it) so the operator sees WHICH
  // integration is churning, not just that provisioning churned.
  const integrationLabel =
    (klass === 'provisioning' || klass === 'operator-config') && integrationKey
      ? ` for integration '${integrationKey}'`
      : '';
  return (
    `Circuit breaker tripped: ${events.length} ${classLabel}restarts in ${windowLabel}` +
    `${integrationLabel} (${breakdown}); most recent=${last.reason} at ${new Date(last.at).toISOString()}`
  );
}
